import axios from "axios";
import { ApiError, toApiError } from "./apiError";
import {
  ActiveDraft,
  Artist,
  ArtistPrice,
  ArtistSearchResult,
  CurrentLineup,
  DbHealthStatus,
  HealthStatus,
  LineupHistory,
  NextWeekLineup,
  PlayerStats,
} from "./dataTypes";
import {
  CreateGroupBody,
  CreateLineupBody,
  InviteToGroupBody,
  JoinGroupBody,
  LoginBody,
  RegisterBody,
  TradeLineupBody,
} from "./inputTypes";
import {
  AcceptGroupInviteResponse,
  CreateGroupResponse,
  GetGroupResponse,
  CreateLineupResponse,
  GroupLeaderboardResponse,
  GroupOrArtistNewsResponse,
  GroupSearchResponse,
  LineupTransactionsResponse,
  MessageResponse,
  PlayerLineupsResponse,
  PlayerNewsResponse,
  RecruitmentPriceResponse,
  TradeLineupResponse,
  ArtistPointsResponse,
  AuthResponse,
  LimitedPlayerDetails,
  PlayerDetails,
  JoinGroupResponse,
} from "./returnTypes";

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? "/api",
  withCredentials: true,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// =============================================================================
// HTTP helpers
// =============================================================================

type Params = Record<string, string | number | boolean | undefined>;

async function get<T>(path: string, params?: Params): Promise<T> {
  try {
    const { data } = await client.get<T>(path, { params });
    return data;
  } catch (err) {
    toApiError(err);
  }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  try {
    const { data } = await client.post<T>(path, body);
    return data;
  } catch (err) {
    toApiError(err);
  }
}

async function patch<T>(path: string, body?: unknown): Promise<T> {
  try {
    const { data } = await client.patch<T>(path, body);
    return data;
  } catch (err) {
    toApiError(err);
  }
}

async function del<T>(path: string): Promise<T> {
  try {
    const { data } = await client.delete<T>(path);
    return data;
  } catch (err) {
    toApiError(err);
  }
}

// =============================================================================
// Health
// =============================================================================

export function getHealth(): Promise<HealthStatus> {
  return get("/health/");
}

export function getDbHealth(): Promise<DbHealthStatus> {
  return get("/health/db");
}

// =============================================================================
// Drafts
// =============================================================================

export function getActiveDraft(): Promise<ActiveDraft> {
  return get("/drafts/active");
}

// =============================================================================
// Artists
// =============================================================================

export function searchArtists(params: {
  q: string;
  draftId?: string;
  limit?: number;
  offset?: number;
}): Promise<ArtistSearchResult[]> {
  return get("/artists/search", params);
}

export function getArtist(artistId: string): Promise<Artist> {
  return get(`/artists/${encodeURIComponent(artistId)}/`);
}

export function getArtistPrice(
  artistId: string,
  draftId: string,
): Promise<ArtistPrice> {
  return get(`/artists/${encodeURIComponent(artistId)}/price`, { draftId });
}

export function getArtistPoints(
  artistId: string,
  params: { startDate?: string; endDate?: string },
): Promise<ArtistPointsResponse> {
  return get(`/artists/${encodeURIComponent(artistId)}/points`, params);
}

// =============================================================================
// Player
// =============================================================================

export function getPlayer(params?: {
  playerId?: string;
  draftId?: string;
}): Promise<PlayerDetails | LimitedPlayerDetails> {
  return get("/player", params);
}

export async function getMe(): Promise<AuthResponse | null> {
  try {
    return await get<AuthResponse>("/player/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

export function register(body: RegisterBody): Promise<AuthResponse> {
  return post("/player/register", body);
}

export function login(body: LoginBody): Promise<AuthResponse> {
  return post("/player/login", body);
}

export function logout(): Promise<MessageResponse> {
  return post("/player/logout");
}

export function deletePlayer(playerId: string): Promise<MessageResponse> {
  return del(`/player/${encodeURIComponent(playerId)}`);
}

export function getPlayerStats(
  playerId: string,
  params: { draftId?: string; startDate?: string; endDate?: string },
): Promise<PlayerStats> {
  return get(`/player/${encodeURIComponent(playerId)}/stats`, params);
}

export function getPlayerLineups(
  playerId: string,
  params: { draftId: string; date?: string },
): Promise<PlayerLineupsResponse> {
  return get(`/player/${encodeURIComponent(playerId)}/lineups`, params);
}

// =============================================================================
// Lineup
// =============================================================================

export function createLineup(
  body: CreateLineupBody,
): Promise<CreateLineupResponse> {
  return post("/lineup/", body);
}

export function getCurrentLineup(
  playerId: string,
  params: { draftId: string; lineupIndex: number },
): Promise<CurrentLineup> {
  return get(`/lineup/${encodeURIComponent(playerId)}/current`, params);
}

export function getNextWeekLineup(
  playerId: string,
  params: { draftId: string; lineupIndex: number },
): Promise<NextWeekLineup> {
  return get(`/lineup/${encodeURIComponent(playerId)}/next-week`, params);
}

export function getLineupHistory(
  playerId: string,
  params: { draftId: string; lineupIndex: number; date: string },
): Promise<LineupHistory> {
  return get(`/lineup/${encodeURIComponent(playerId)}/history`, params);
}

export function tradeLineup(
  body: TradeLineupBody,
): Promise<TradeLineupResponse> {
  return post("/lineup/trade", body);
}

export function getLineupTransactions(
  playerId: string,
  params: { draftId: string; lineupIndex: number },
): Promise<LineupTransactionsResponse> {
  return get(`/lineup/${encodeURIComponent(playerId)}/transactions`, params);
}

export function getRecruitmentPrice(
  playerId: string,
  artistId: string,
  params: { draftId: string; lineupIndex: number },
): Promise<RecruitmentPriceResponse> {
  return get(
    `/lineup/${encodeURIComponent(playerId)}/recruitment-price/${encodeURIComponent(artistId)}`,
    params,
  );
}

// =============================================================================
// Groups
// =============================================================================

export function getGroup(groupId: string): Promise<GetGroupResponse> {
  return get(`/group/${encodeURIComponent(groupId)}`);
}

export function createGroup(
  body: CreateGroupBody,
): Promise<CreateGroupResponse> {
  return post("/group/", body);
}

export function deleteGroup(groupId: string): Promise<MessageResponse> {
  return del(`/group/${encodeURIComponent(groupId)}`);
}

export function searchGroups(params: {
  draftId: string;
  q: string;
  limit?: number;
  offset?: number;
}): Promise<GroupSearchResponse> {
  return get("/group/search", params);
}

export function joinGroup(
  groupId: string,
  body?: JoinGroupBody,
): Promise<JoinGroupResponse> {
  return post(`/group/join/${encodeURIComponent(groupId)}`, body);
}

export function inviteToGroup(
  groupId: string,
  body: InviteToGroupBody,
): Promise<MessageResponse> {
  return post(`/group/invite/${encodeURIComponent(groupId)}`, body);
}

export function acceptGroupInvite(
  inviteId: string,
): Promise<AcceptGroupInviteResponse> {
  return post(`/group/accept/${encodeURIComponent(inviteId)}`);
}

// TODO: decline Group Invite
// TODO: get Group Invites

export function removeGroupMember(
  groupId: string,
  playerId: string,
): Promise<MessageResponse> {
  return del(
    `/group/${encodeURIComponent(groupId)}/members/${encodeURIComponent(playerId)}`,
  );
}

export function getGroupLeaderboard(
  groupId: string,
  params?: { date?: string; limit?: number; offset?: number },
): Promise<GroupLeaderboardResponse> {
  return get(`/group/${encodeURIComponent(groupId)}/leaderboard`, params);
}

// =============================================================================
// News
// =============================================================================

export function getPlayerNews(params?: {
  limit?: number;
  offset?: number;
}): Promise<PlayerNewsResponse> {
  return get("/news/player", params);
}

export function markNewsRead(newsId: string): Promise<MessageResponse> {
  return patch(`/news/read/${encodeURIComponent(newsId)}`);
}

export function getArtistNews(
  artistId: string,
  params?: { limit?: number; offset?: number },
): Promise<GroupOrArtistNewsResponse> {
  return get(`/news/artist/${encodeURIComponent(artistId)}`, params);
}

export function getGroupNews(
  groupId: string,
  params?: { limit?: number; offset?: number },
): Promise<GroupOrArtistNewsResponse> {
  return get(`/news/group/${encodeURIComponent(groupId)}`, params);
}
